<template>
    <div class="tw:flex tw:flex-col tw:gap-6">
        <h3 class="tw:text-lg tw:font-semibold tw:text-gray-800 tw:m-0">
            {{ $t('perfil_invitaciones.resumen_titulo') }}
        </h3>

        <!-- Resumen del evento -->
        <CardFront v-if="evento" :titleNotBorder="evento.nombre || evento.titulo">
            <p v-if="evento.fecha" class="tw:text-xs tw:text-gray-500 tw:m-0">{{ evento.fecha }}</p>

            <!-- Lista de butacas -->
            <div v-if="asientosSeleccionados.length" class="tw:flex tw:flex-col tw:gap-2 tw:pt-2 tw:border-t tw:border-gray-100">
                <div
                    v-for="(asiento, idx) in asientosSeleccionados"
                    :key="asiento.id"
                    class="tw:flex tw:items-center tw:justify-between tw:py-1"
                >
                    <div class="tw:flex tw:flex-col">
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-widest tw:text-gray-500">
                            {{ $t('perfil_invitaciones.invitacion_n', { n: idx + 1 }) }}
                        </span>
                        <div class="tw:flex tw:gap-3 tw:items-baseline">
                            <span v-if="zonaNombre" class="tw:text-xs tw:font-semibold tw:text-gray-700">{{ zonaNombre }}</span>
                            <span class="tw:text-sm tw:font-bold tw:text-gray-900">
                                {{ $t('perfil_invitaciones.fila') }}: {{ asiento.row || asiento.fila }}
                            </span>
                            <span class="tw:text-sm tw:font-bold tw:text-gray-900">
                                {{ $t('perfil_invitaciones.butaca') }}: {{ asiento.number || asiento.numero }}
                            </span>
                        </div>
                    </div>
                    <span class="tw:text-sm tw:font-bold tw:text-gray-600">
                        {{ formatearPrecio(0) }}
                    </span>
                </div>
            </div>
        </CardFront>

        <!-- Datos del comprador + invitados en row (column en móvil) -->
        <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4">
            <!-- Datos del comprador -->
            <CardFront custom-classes="tw:flex-1">
                <div class="tw:flex tw:items-center tw:gap-2 tw:mb-3">
                    <i class="material-icons tw:text-teal-600 tw:text-lg">person</i>
                    <span class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-500">
                        {{ $t('perfil_invitaciones.datos_comprador') }}
                    </span>
                </div>
                <div class="tw:flex tw:flex-col tw:gap-1.5">
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <i class="material-icons tw:text-gray-400 tw:text-sm">badge</i>
                        <span class="tw:text-sm tw:font-medium">{{ form.nombre }} {{ form.apellidos }}</span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <i class="material-icons tw:text-gray-400 tw:text-sm">email</i>
                        <span class="tw:text-xs tw:text-gray-600">{{ form.email }}</span>
                    </div>
                    <div v-if="form.telefono" class="tw:flex tw:items-center tw:gap-2">
                        <i class="material-icons tw:text-gray-400 tw:text-sm">phone</i>
                        <span class="tw:text-xs tw:text-gray-600">{{ form.telefono }}</span>
                    </div>
                </div>
            </CardFront>

            <!-- Datos de invitados -->
            <CardFront
                v-if="datosPorButaca.length"
                custom-classes="tw:flex-1"
            >
                <div class="tw:flex tw:items-center tw:gap-2 tw:mb-3">
                    <i class="material-icons tw:text-indigo-600 tw:text-lg">groups</i>
                    <span class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-500">
                        {{ $t('perfil_invitaciones.datos_invitados') }}
                    </span>
                </div>
                <div
                    v-for="(datos, idx) in datosPorButaca"
                    :key="idx"
                    class="tw:flex tw:flex-col tw:gap-1 tw:py-1.5"
                    :class="{ 'tw:border-t tw:border-gray-100 tw:mt-1 tw:pt-2': idx > 0 }"
                >
                    <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-widest tw:text-gray-400">
                        {{ $t('perfil_invitaciones.invitacion_n', { n: idx + 1 }) }}
                    </span>
                    <div class="tw:flex tw:items-center tw:gap-2">
                        <i class="material-icons tw:text-gray-400 tw:text-sm">badge</i>
                        <span class="tw:text-sm tw:font-medium">{{ datos.nombre }} {{ datos.apellidos }}</span>
                    </div>
                    <div v-if="datos.telefono" class="tw:flex tw:items-center tw:gap-2">
                        <i class="material-icons tw:text-gray-400 tw:text-sm">phone</i>
                        <span class="tw:text-xs tw:text-gray-600">{{ datos.telefono }}</span>
                    </div>
                </div>
            </CardFront>
        </div>

        <!-- Pago requerido: si el base se renueva con pago a plazos, en este paso
        no se pide tarjeta — el pago se hace en el flujo de plazos. Mostramos un
        aviso al usuario para que sepa que el cargo NO se hace ahora. -->
        <div
            v-if="pagoRequerido"
            class="tw:flex tw:items-center tw:gap-2 tw:p-3 tw:rounded-lg tw:bg-amber-50 tw:border tw:border-amber-200 tw:text-amber-800 tw:text-sm"
        >
            <i class="material-icons tw:text-amber-600">info</i>
            <span>{{ $t('perfil_invitaciones.pago_requerido_msg') }}</span>
        </div>

        <!-- Total a pagar -->
        <CardFront>
            <div class="tw:flex tw:items-center tw:justify-between">
                <span class="tw:text-sm tw:font-semibold tw:text-gray-600">
                    {{ $t('evento.total_pagar') }}
                </span>
                <span class="tw:text-2xl tw:font-extrabold tw:text-teal-600">
                    {{ formatearPrecio(amount) }}
                </span>
            </div>
        </CardFront>

        <!-- Términos y condiciones (oculto cuando pagoRequerido: si el pago se hace
        en otro flujo, el stepper de invitaciones no debe pedir doble aceptación) -->
        <FormTerminos
            v-if="!pagoRequerido"
            :valor="terminosAceptados"
            :mostrar-error="mostrarErrores"
            :disabled="loadingProcesar"
            @update:valor="$emit('update:terminos-aceptados', $event)"
        />

        <!-- Error al procesar -->
        <div
            v-if="error"
            class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm"
        >
            {{ error }}
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import FormTerminos from '@/modules/front/ui/FormTerminos.vue';
    import { useFilters } from '@/composables/front/formatters/useFilters';

    const { formatearPrecio } = useFilters();

    const props = defineProps({
        evento: {
            type: Object,
            default: null,
        },
        asientosSeleccionados: {
            type: Array,
            default: () => [],
        },
        form: {
            type: Object,
            required: true,
        },
        datosPorButaca: {
            type: Array,
            default: () => [],
        },
        zonaSeleccionada: {
            type: Object,
            default: null,
        },
        amount: {
            type: [Number, String],
            default: 0,
        },
        terminosAceptados: {
            type: Object,
            default: () => ({
                privacidad: false,
                condiciones: false,
                comunicaciones: false,
                plazos: false,
            }),
        },
        mostrarErrores: {
            type: Boolean,
            default: false,
        },
        pagoRequerido: {
            type: Boolean,
            default: false,
        },
        loadingProcesar: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Boolean],
            default: null,
        },
    });

    const emit = defineEmits(['update:terminos-aceptados']);

    const zonaNombre = computed(() =>
        props.zonaSeleccionada?.name ?? props.zonaSeleccionada?.nombre ?? null
    );
</script>
